import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  Query,
} from '@angular/fire/compat/firestore';
import {
  BattleResult,
  CcpcGameResult,
  GamePool,
  GameProject,
  GameType,
  User,
  GameLanguage,
} from '@codecraft-works/data-models';
import firebase from 'firebase/compat/app';
import {
  Observable,
  combineLatest,
  from,
  lastValueFrom,
  firstValueFrom,
} from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable()
export class GameProjectService {
  private gameTypeEnum = GameType;
  environment = environment;

  /**
   * Initializes required services and database references
   * @param db
   * @param firepadDb
   * @param httpClient
   */
  constructor(
    private db: AngularFirestore,
    private firepadDb: AngularFireDatabase,
    private httpClient: HttpClient
  ) {}

  /**
   * Return Game Projects as Observable with optional userID
   * @param userID
   */
  public getGameProjects(options?: {
    language?: string;
    game?: string;
    uid?: string;
    root?: string;
    limit?: number;
  }): Observable<GameProject[]> {
    options.limit = options.limit ? options.limit : 100;
    let gameCollectionRef: AngularFirestoreCollection<GameProject>;
    if (options.uid) {
      if (options.root) {
        gameCollectionRef = this.db.collection<GameProject>(
          'game-projects',
          (ref) =>
            ref
              .where('uid', '==', options.uid)
              .where('root', '==', options.root)
              .orderBy('modified', 'desc')
              .limit(options.limit)
        );
      } else if (options.language && options.game) {
        gameCollectionRef = this.db.collection<GameProject>(
          'game-projects',
          (ref) =>
            ref
              .where('uid', '==', options.uid)
              .where('type', '==', options.game)
              .where('language', '==', options.language)
              .orderBy('modified', 'desc')
              .limit(options.limit)
        );
      } else {
        // If given userID, return game projects that contain a matching `uid`
        gameCollectionRef = this.db.collection<GameProject>(
          'game-projects',
          (ref) =>
            ref
              .where('uid', '==', options.uid)
              .orderBy('modified', 'desc')
              .limit(options.limit)
        );
      }
    } else {
      // Else return all public game projects
      gameCollectionRef = this.db.collection<GameProject>(
        'game-projects',
        (ref) =>
          ref
            .where('public', '==', true)
            .orderBy('modified', 'desc')
            .limit(options.limit)
      );
    }

    return gameCollectionRef.valueChanges();
  }

  /**
   * Return entire Game Project's collection
   */
  private getData() {
    return this.db.collection<GameProject>('game-projects');
  }

  /**
   * Return Game Project's data via id
   * @param gameProject
   */
  private getGameProjectData(gameProject: GameProject) {
    return this.db.doc<GameProject>(gameProject.id);
  }

  /**
   * Returns and Observable of the Game Project via id
   * @param id
   */
  public getGameProject(id: string): Observable<GameProject> {
    const gameProject = this.db
      .collection('game-projects')
      .doc<GameProject>(id)
      .valueChanges();

    return gameProject;
  }

  /**
   * Creates game project with required contents and then inserted into db with corresponding userId
   * @param user
   * @param authUser
   */
  async insertGameProject(
    user: User,
    gameType: GameType,
    gameLanguage: GameLanguage
  ) {
    let codeMap = null;

    // Picks starter editor code based on language and game type
    switch (gameLanguage) {
      case 'java':
        switch (gameType) {
          case this.gameTypeEnum.TICTACTOE:
            codeMap = [
              'import ticTacToe.Player;',
              '',
              'public class TestPlayer extends Player {',
              '',
              '  public void calcMove() {',
              '    // Function that returns two-dimensional list of chars on the tic tac toe board',
              '    // Empty tiles will be represented as a space char, X by X character and O by O char',
              '    // The coordinates of the Board are arranged in the following way:',
              '    // [0][0]  [0][1]  [0][2]',
              '    // [1][0]  [1][1]  [1][2]',
              '    // [2][0]  [2][1]  [2][2]',
              '    // For example, board[0][2] would return the value of the upper right-most square',
              '    char[][] board = getGameState();',
              '',
              "    // Get your team symbol ('X' or 'O')",
              '    char team = getTeam();',
              '',
              '    // Choose a random empty position',
              '    int row = (int)(Math.random() * 3);',
              '    int col = (int)(Math.random() * 3);',
              "    while (board[row][col] != ' ') {",
              '      row = (int)(Math.random() * 3);',
              '      col = (int)(Math.random() * 3);',
              '    }',
              '',
              '    // Submit your move',
              '    submitMove(row, col);',
              '  }',
              '}',
            ].join('\n');
            break;
          case this.gameTypeEnum.BATTLESHIP:
            codeMap = [
              'import battleship.Player;',
              'import battleship.Maps.mapStates;',
              'import java.util.Random;',
              '',
              'public class TestPlayer extends Player {',
              '',
              '  public void calcMove() {',
              '    // Get the current game state.',
              '    // shadowMap[r][c] will be either:',
              '    // - mapStates.EMPTY',
              '    // - mapStates.HIT',
              '    // - mapStates.MISS',
              '    // - mapStates.SUNK',
              '    mapStates[][] shadowMap = getShadowMap();',
              '',
              '    // Choose a random empty position',
              '    int row = (int)(Math.random() * SIZE);',
              '    int col = (int)(Math.random() * SIZE);',
              '    while (shadowMap[row][col] != mapStates.EMPTY) {',
              '      row = (int)(Math.random() * SIZE);',
              '      col = (int)(Math.random() * SIZE);',
              '    }',
              '',
              '    // Submit your move',
              '    submitMove(row, col);',
              '  }',
              '}',
            ].join('\n');
            break;
        }
        break;
      case 'python':
        switch (gameType) {
          case this.gameTypeEnum.TICTACTOE:
            codeMap = [
              'import random',
              'from tic_tac_toe.player import Player',
              '',
              'class CodecraftPlayer(Player):',
              '    ',
              '    def calc_move(self):',
              '        # Function that returns two-dimensional list of chars on the tic tac toe board',
              '        # Empty tiles will be represented as a space char, X by X character and O by O char',
              '        # The coordinates of the Board are arranged in the following way:',
              '        # [0][0]  [0][1]  [0][2]',
              '        # [1][0]  [1][1]  [1][2]',
              '        # [2][0]  [2][1]  [2][2]',
              '        # For example, board[0][2] would return the value of the upper right-most square',
              '        board = self.get_game_state()',
              '        ',
              "        # Get your team symbol ('X' or 'O')",
              '        team = self.get_team()',
              '        ',
              '        # Choose a random empty position',
              '        row = random.randint(0, 2)',
              '        col = random.randint(0, 2)',
              '        while board[row][col] != " ":',
              '            row = random.randint(0, 2)',
              '            col = random.randint(0, 2)',
              '        ',
              '        # Submit your move',
              '        self.submit_move(row, col)',
            ].join('\n');
            break;
          case this.gameTypeEnum.BATTLESHIP:
            break;
        }
        break;
      case 'javascript':
        switch (gameType) {
          case this.gameTypeEnum.TICTACTOE:
            break;
          case this.gameTypeEnum.BATTLESHIP:
            break;
        }
        break;
      case 'blockly':
        switch (gameType) {
          case this.gameTypeEnum.TICTACTOE:
            codeMap =
              '<xml><variables><variable id="%9aRn-4G_kPe-?aw8A`o">i</variable><variable id="y_JE_H9IRB|MMGatoQwp">j</variable></variables><block type="tictactoe_onmyturn" id="?a2vS+}KssepxFk%(,0!" x="65" y="109"><statement name="calc_move"><block type="controls_for" id="DE-OugF);hiv*a3VMO3s"><field name="VAR" id="%9aRn-4G_kPe-?aw8A`o">i</field><value name="FROM"><block type="math_number" id="Max:PVI$pA8bvsOP/vr}"><field name="NUM">0</field></block></value><value name="TO"><block type="math_number" id="K.WukbX%8J^bgUnkL1PT"><field name="NUM">2</field></block></value><value name="BY"><block type="math_number" id="Uvu3#^,r%YZb,li3lM,H"><field name="NUM">1</field></block></value><statement name="DO"><block type="controls_for" id="Y((q=.K!;5Tm.X^;PS7N"><field name="VAR" id="y_JE_H9IRB|MMGatoQwp">j</field><value name="FROM"><block type="math_number" id="-`z?@16@OKmIz0#@+@Ll"><field name="NUM">0</field></block></value><value name="TO"><block type="math_number" id="By#RBdAQ*pq(WD8YnSpj"><field name="NUM">2</field></block></value><value name="BY"><block type="math_number" id="T-DZNxOAOx,8aZX8WW}j"><field name="NUM">1</field></block></value><statement name="DO"><block type="controls_if" id="@~J.7?I@AVv*nr~rOB[5"><value name="IF0"><block type="logic_compare" id="`)4m@.}!qk5AR~B9q;Q)"><field name="OP">EQ</field><value name="A"><block type="tictactoe_checkboard" id="6k7^0DfeVaj}|6YF^FQH"><value name="ROW"><block type="variables_get" id="6d#J)zU.l@oD{ySv}]#,"><field name="VAR" id="%9aRn-4G_kPe-?aw8A`o">i</field></block></value><value name="COL"><block type="variables_get" id="O}N:c)q,n^Z)rr8BG#gg"><field name="VAR" id="y_JE_H9IRB|MMGatoQwp">j</field></block></value></block></value><value name="B"><block type="tictactoe_empty" id="P/;q}u1HYQo,05puEvR*"></block></value></block></value><statement name="DO0"><block type="tictactoe_submitmove" id="zr5fxHdGoQ]1ZOG!`GfB"><value name="ROW"><block type="variables_get" id="a52w:klD`T,4(X20obNm"><field name="VAR" id="%9aRn-4G_kPe-?aw8A`o">i</field></block></value><value name="COL"><block type="variables_get" id="_A[WE!a$K[MMR.x[faJR"><field name="VAR" id="y_JE_H9IRB|MMGatoQwp">j</field></block></value></block></statement></block></statement></block></statement></block></statement></block></xml>';
            break;
          case this.gameTypeEnum.BATTLESHIP:
            break;
        }
        break;
      default:
        break;
    }

    const newGameProject = {
      id: this.db.createId(),
      created: firebase.firestore.Timestamp.now(),
      modified: firebase.firestore.Timestamp.now(),
      name: 'New Game',
      uid: user.uid,
      authid: user.uid,
      user: {
        displayName: user.displayName,
        photoURL: user.photoURL,
        userName: user.userName || null,
        uid: user.uid,
      },
      public: false,
      type: gameType,
      code: codeMap,
      result: 'No results yet.',
      language: gameLanguage,
      readme: 'This is a readme.',
      lock: false,
      currentActiveUsers: [],
      collaborators: [],
      featured: false,
      collabOn: false,
    };
    await this.getData().doc(newGameProject.id).set(newGameProject);
    return newGameProject.id;
  }

  /**
   * Fork function included in case we want to consider forking games later.
   * @param oldGameProject
   * @param forUser
   * @param authUser
   * @returns
   */
  public forkGameProject(
    oldGameProject: GameProject,
    forUser: User,
    authUser: User = forUser
  ): string {
    const newGameProject = {
      id: this.db.createId(),
      created: firebase.firestore.Timestamp.now(),
      modified: firebase.firestore.Timestamp.now(),
      name: 'Cloned:' + oldGameProject.name,
      uid: forUser.uid,
      authid: authUser.uid,
      user: {
        displayName: forUser.displayName,
        photoURL: forUser.photoURL,
        userName: forUser.userName || null,
        uid: forUser.uid,
      },
      public: oldGameProject.public,
      type: oldGameProject.type,
      code: oldGameProject.code,
      result: 'No results yet.',
      language: oldGameProject.language,
      readme: oldGameProject.readme,
      lock: false,
      currentActiveUsers: [],
      collaborators: [],
      featured: false,
      collabOn: false,
      root: oldGameProject.id,
    };
    this.getData().doc(newGameProject.id).set(newGameProject);
    return newGameProject.id;
  }

  /**
   * deletes a game project based on subscription
   * @param gameProject
   */
  /* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
  public deleteGameProject(gameProject: GameProject): Observable<void> {
    if (gameProject) {
      return from(this.getGameProjectData(gameProject).delete());
    } else {
      throw new Error("Can't delete without a key.");
    }
  }

  /**
   * Delete the Game Project from the Firestore
   * @param id
   */
  public deleteDatabaseProject(id: string) {
    if (id) {
      return from(this.db.collection('game-projects').doc(id).delete());
    } else {
      throw new Error("Can't delete without a key.");
    }
  }

  /**
   * Return Firepad via Game Project's id
   * @param gameProjectID
   */
  public getFirepadRef(gameProjectID: string) {
    return this.firepadDb.database.ref(`game-project-firepad/${gameProjectID}`);
  }

  /**
   * Return Firepad via Game Project's id
   * @param gameProjectID
   */
  public getFirepadRefChanges(gameProjectId: string): Observable<any> {
    const code$: Observable<any> = this.firepadDb
      .list(`game-project-firepad/${`${gameProjectId}`}`)
      .valueChanges();

    return code$;
  }

  public addGameToPool(gameProject: GameProject): Promise<string> {
    let fileContentType = '';
    let fileExtension = '';
    let code = '';
    let prefix = '';

    const docId = this.db.createId();

    switch (gameProject.language) {
      case 'java':
        fileContentType = 'text/x-java-source';
        fileExtension = 'java';
        prefix = 'CW_';
        code = this.changeJavaClassName({
          key: docId,
          editorCode: gameProject.code,
          prefix: prefix,
        });
        break;
      case 'blockly':
        fileContentType = 'text/x-python';
        fileExtension = 'py';
        code = gameProject.compiledCode;
        break;
      case 'python':
        fileContentType = 'text/x-python';
        fileExtension = 'py';
        code = gameProject.code;
        break;
      default:
        break;
    }

    return lastValueFrom(
      this.httpClient.post(
        '/api/games/saveGame',
        {
          sourceCode: code,
          filename: `${prefix}${docId}.${fileExtension}`,
          contentType: fileContentType,
        },
        { responseType: 'text' }
      )
    )
      .then(async () => {
        await this.db
          .collection('game-pool')
          .doc(`${docId}`)
          .set({
            id: docId,
            uid: gameProject.uid,
            user: {
              uid: gameProject.user.uid,
              displayName: gameProject.user.displayName,
              photoURL: gameProject.user.photoURL,
              userName: gameProject.user.userName || null,
            },
            modified: firebase.firestore.Timestamp.now(),
            created: firebase.firestore.Timestamp.now(),
            name: gameProject.name,
            root: gameProject.id,
            project: gameProject,
            public: true,
            statistics: {
              wins: 0,
              losses: 0,
              p1Wins: 0,
              p2Wins: 0,
              ...(gameProject.type === GameType.TICTACTOE
                ? {
                    time: 0,
                    turns: 0,
                    averageTime: 0,
                  }
                : {}),
              invalidMoves: 0,
              errors: 0,
              total: 0,
              winProportion: 0,
              errorProportion: 0,
            },
          });
      })
      .then(() => {
        return docId;
      });
  }

  public getGameFromPool(id: string): Observable<GamePool> {
    return this.db
      .collection<GamePool>('game-pool')
      .doc<GamePool>(id)
      .valueChanges();
  }

  public getGamesFromPool(options?: {
    uid?: string;
    type?: GameType;
    language?: string;
    root?: string;
    publicOnly?: boolean;
    excludeUser?: string;
  }): Observable<GamePool[]> {
    return this.db
      .collection<GamePool>('game-pool', (ref) => {
        let query: Query = ref;

        if (options?.publicOnly === false) {
          null;
        } else {
          query = query.where('public', '==', true);
        }

        if (options?.uid) {
          query = query.where('uid', '==', options.uid);
        }

        if (options?.excludeUser) {
          query = query.where('uid', '!=', options.excludeUser);
        }

        if (options?.type) {
          query = query.where('project.type', '==', options.type);
        }

        if (options?.language) {
          query = query.where('project.language', '==', options.language);
        }

        if (options?.root) {
          query = query.where('root', '==', options.root);
        }

        query = query.orderBy('uid', 'desc');

        // query = query.orderBy('modified', 'desc');

        return query;
      })
      .valueChanges();
  }

  changeJavaClassName(options: {
    key: string;
    editorCode: string;
    prefix: string;
  }): string {
    return options.editorCode.replace(
      /(class\s)(\w*)([\w\s]*)(?=extends Player)(extends Player)/g,
      `$1${options.prefix}${options.key}$3$4`
    );
  }

  /**
   * Saves the text in the editor
   * @param {string} key The ID of the game project
   * @param {string} editorCode The code in the editor
   * @param {string} language The language of the code
   * @param {string} [compiledCode] The compiled code
   * @returns {Promise<void>}
   */

  async saveEditorText(options: {
    key: string;
    editorCode: string;
    compiledCode?: string;
    language: string;
  }): Promise<void> {
    const { key, editorCode, compiledCode, language } = options;
    const gameProjectDocRef = this.db.collection('game-projects').doc(key);

    let fileContentType = '';
    let fileExtension = '';
    let code = '';
    let prefix = '';

    switch (language) {
      case 'java':
        fileContentType = 'text/x-java-source';
        fileExtension = 'java';
        prefix = 'CW_';
        code = this.changeJavaClassName({ prefix, key, editorCode });
        break;
      case 'blockly':
        fileContentType = 'text/x-python';
        fileExtension = 'py';
        code = compiledCode;
        break;
      case 'python':
        fileContentType = 'text/x-python';
        fileExtension = 'py';
        code = editorCode;
        break;
      default:
        break;
    }

    await lastValueFrom(
      this.httpClient.post(
        '/api/games/saveGame',
        {
          sourceCode: code,
          filename: `${prefix}${key}.${fileExtension}`,
          contentType: fileContentType,
        },
        { responseType: 'text' }
      )
    ).then(async () => {
      await gameProjectDocRef.set(
        compiledCode
          ? {
              code: editorCode,
              compiledCode: compiledCode,
              modified: firebase.firestore.Timestamp.now(),
            }
          : {
              code: editorCode,
              modified: firebase.firestore.Timestamp.now(),
            },
        { merge: true }
      );
    });
  }

  public setCollab(id: string, collabFlag: boolean) {
    this.db.collection('game-projects').doc(id).update({
      collabOn: collabFlag,
      modified: firebase.firestore.Timestamp.now(),
    });
  }

  public setCloudVariable(
    gameProjectId: string,
    jsonString: string,
    uid: string
  ) {
    // To get size remove spaces from string and get text encoding length
    const jsonSize = new TextEncoder().encode(
      jsonString.replace(/\s/g, '')
    ).length;

    if (jsonSize / 1024 > 1) {
      // eslint-disable-next-line no-throw-literal
      throw 'sizeExceeded';
    }

    const jsonObj = JSON.parse(jsonString);

    return this.firepadDb
      .object('cloud-variable/' + gameProjectId)
      .set({ uid: uid, value: jsonObj });
  }

  public getCloudVariable(gameProjectId: string) {
    return this.firepadDb
      .object('cloud-variable/' + gameProjectId + '/' + 'value')
      .valueChanges();
  }

  public runCode(
    project$: Observable<GameProject>,
    user: User
  ): Promise<CcpcGameResult | null> {
    return lastValueFrom(
      project$.pipe(take(1)).pipe(
        switchMap(async (project) => {
          let functionCallURL = '';
          switch (project.type) {
            case this.gameTypeEnum.TICTACTOE:
              switch (project.language) {
                case 'java':
                  if (window.location.host === 'localhost:8000') {
                    functionCallURL = 'http://localhost:5011/';
                  } else {
                    functionCallURL = '/api/v3/games/tic-tac-toe/java';
                  }
                  break;

                case 'blockly':
                case 'python':
                  if (window.location.host === 'localhost:8000') {
                    functionCallURL =
                      'http://localhost:5010/api/v3/games/tic-tac-toe/python';
                  } else {
                    functionCallURL = '/api/v3/games/tic-tac-toe/python';
                  }
                  break;

                case 'javascript':
                  functionCallURL = '/api/games/tic-tac-toe/javascript';
                  break;

                default:
                  break;
              }
              break;
            case this.gameTypeEnum.BATTLESHIP:
              switch (project.language) {
                case 'java':
                  if (window.location.host === 'localhost:8000') {
                    functionCallURL = 'http://localhost:5012/';
                  } else {
                    functionCallURL = '/api/v3/games/hide-and-sink/java';
                  }
                  break;

                case 'blockly':
                case 'python':
                  functionCallURL = '/api/games/battleship/python';
                  break;

                case 'javascript':
                  functionCallURL = '/api/games/battleship/javascript';
                  break;

                default:
                  break;
              }
              break;

            default:
              break;
          }

          const gameData = {
            playerId: project.id,
            playerName: user.displayName,
            playerCode: project.id,
            type: project.language === 'blockly' ? 'blockly' : project.type,
          };

          if (functionCallURL) {
            const headers = new HttpHeaders({
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            });

            const result: Promise<CcpcGameResult> = lastValueFrom(
              this.httpClient.post(functionCallURL, gameData, {
                responseType: 'json',
                headers: headers,
              })
            ).then((res) => {
              return res as CcpcGameResult;
            });

            return result;
          } else {
            return null;
          }
        })
      )
    );
  }

  /**
   * Runs a battle between two game pool submissions
   */
  public runBattle(
    player1GamePool: GamePool,
    player2GamePool: GamePool
  ): Promise<BattleResult> {
    return firstValueFrom(
      this.httpClient.post(
        '/api/games/runBattle',
        { player1GamePool, player2GamePool },
        { responseType: 'json' }
      )
    ).then((response) => {
      return firstValueFrom(
        this.firepadDb
          .object<BattleResult>(`battle-results/${response['taskId']}`)
          .valueChanges()
          .pipe(filter((battleResult) => battleResult !== null))
      );
    });
  }

  /**
   * Retrieves a BattleResult
   * @param id The BattleResult ID (same as corresponding RunBattleTask ID)
   */
  public getBattleResultFromId(id): Promise<BattleResult> {
    return firstValueFrom(
      this.firepadDb.object<BattleResult>(`battle-results/${id}`).valueChanges()
    );
  }

  /**
   * Retrieves all the BattleResults for a GamePool
   * @param id The GamePool's ID
   */
  public getBattleResultsFromPool(id) {
    const battleIds$ = this.firepadDb
      .object(`game-pools/${id}/battles`)
      .valueChanges()
      .pipe(
        map((battlesObject) => {
          return Object.keys(battlesObject || {});
        })
      );

    const battleResults$ = battleIds$.pipe(
      switchMap((battleIds) =>
        combineLatest(
          battleIds.map((battleId) =>
            this.firepadDb
              .object<BattleResult>(`battle-results/${battleId}`)
              .valueChanges()
          )
        )
      ),
      map((battleResults) =>
        battleResults.filter((battleResult) => battleResult !== null)
      )
    );

    return battleResults$;
  }

  /**
   * Convert existing Blockly Project to new Python Project
   * @param oldWebProject
   * @param forUser
   * @param authUser
   */
  public async convertBlocklyProject(
    oldBlocklyProject: GameProject,
    forUser: User,
    authUser: User = forUser
  ): Promise<string> {
    const newPythonProject = {
      id: this.db.createId(),
      created: firebase.firestore.Timestamp.now(),
      modified: firebase.firestore.Timestamp.now(),
      name: 'Converted:' + oldBlocklyProject.name,
      uid: forUser.uid,
      authid: authUser.uid,
      user: {
        displayName: forUser.displayName,
        photoURL: forUser.photoURL,
        userName: forUser.userName || null,
        uid: forUser.uid,
      },
      public: oldBlocklyProject.public,
      type: oldBlocklyProject.type,
      code: oldBlocklyProject.compiledCode,
      result: 'No results yet.',
      language: 'python',
      readme: oldBlocklyProject.readme,
      lock: false,
      currentActiveUsers: [],
      collaborators: [],
      featured: false,
      collabOn: false,
      root: oldBlocklyProject.id,
    };
    await this.getData().doc(newPythonProject.id).set(newPythonProject);
    return newPythonProject.id;
  }

  public getLeaderboard(opts?: {
    language: GameLanguage;
    type: GameType;
  }): Observable<GamePool[]> {
    return this.db
      .collection<GamePool>('game-pool', (ref) => {
        let query: Query = ref;

        if (opts?.type) {
          query = query.where('project.type', '==', opts.type);
        }

        if (opts?.language) {
          query = query.where('project.language', '==', opts.language);
        }

        query = query
          .orderBy('statistics.winProportion', 'desc')
          .where('public', '==', true)
          .limit(100);

        return query;
      })
      .valueChanges();
  }
}
